import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Edit2, Plus, Trash2, InfoIcon } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Config } from '@/types';
import { Label } from '@/components/ui/label';
import { useState } from 'react';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
interface Configs {
  configs: Config[];
  handleSave: (config: Config) => void;
}

type AstronCourse = {
  clubId: string;
  courseId: string;
};

const Configs = ({ configs, handleSave }: Configs) => {
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValues, setEditValues] = useState<Record<number, string>>({});
  const [astronCourses, setAstronCourses] = useState<Record<number, AstronCourse[]>>({});

  const handleEditClick = (config: Config) => {
    setEditingId(config.id);

    // Se for AstronCourses, parse o JSON
    if (config.key === 'AstronCourses') {
      try {
        const parsed = JSON.parse(config.value || '[]');
        setAstronCourses({ [config.id]: Array.isArray(parsed) ? parsed : [] });
      } catch {
        setAstronCourses({ [config.id]: [] });
      }
      setEditValues({ [config.id]: config.value });
    } else {
      setEditValues({ [config.id]: config.value });
    }
  };

  const handleValueChange = (id: number, value: string) => {
    setEditValues({ ...editValues, [id]: value });
  };

  const handleAstronCourseChange = (
    id: number,
    index: number,
    field: 'clubId' | 'courseId',
    value: string,
  ) => {
    const courses = astronCourses[id] || [];
    const updated = [...courses];
    updated[index] = { ...updated[index], [field]: value };
    setAstronCourses({ ...astronCourses, [id]: updated });

    // Atualiza o valor JSON automaticamente
    setEditValues({ ...editValues, [id]: JSON.stringify(updated) });
  };

  const handleAddAstronCourse = (id: number) => {
    const courses = astronCourses[id] || [];
    const updated = [...courses, { clubId: '', courseId: '' }];
    setAstronCourses({ ...astronCourses, [id]: updated });
    setEditValues({ ...editValues, [id]: JSON.stringify(updated) });
  };

  const handleRemoveAstronCourse = (id: number, index: number) => {
    const courses = astronCourses[id] || [];
    const updated = courses.filter((_, i) => i !== index);
    setAstronCourses({ ...astronCourses, [id]: updated });
    setEditValues({ ...editValues, [id]: JSON.stringify(updated) });
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditValues({});
    setAstronCourses({});
  };

  const handleSaveConfig = (config: Config) => {
    if (editingId !== null) {
      handleSave({
        ...config,
        value: editValues[editingId] || '',
      });
      setEditingId(null);
      setEditValues({});
      setAstronCourses({});
    }
  };

  return (
    <Card className="flex-1">
      <CardContent>
        <h3 className="text-lg font-bold mb-7 mt-4">Outras Configurações</h3>

        <div className="flex flex-col gap-4 mb-5">
          {configs?.map((ev) => {
            const events = {
              total_event: 'Total Máximo de Eventos',
              time_event: 'Tempo de Envio dos Eventos (em minutos)',
              AstronCourses: 'Turmas Astron (Clube e Curso)',
            };

            const label = events[ev.key] || ev.key;
            const isAstronCourses = ev.key === 'AstronCourses';
            const isAstron = label.includes('Astron');

            return (
              <Sheet>
                <div key={ev.id} className="space-y-3">
                  <div className="flex-row flex items-center gap-3">
                    <Label htmlFor={ev.key}>{label}</Label>
                    <SheetTrigger asChild>
                      {isAstron && (
                        <Button size="icon" variant="outline">
                          <InfoIcon />
                        </Button>
                      )}
                    </SheetTrigger>
                  </div>

                  <SheetContent>
                    <SheetHeader>
                      <SheetTitle>Astron Members</SheetTitle>
                      <SheetDescription>
                        Instruções para adicionar novas turmas da Astron Members.
                      </SheetDescription>
                    </SheetHeader>

                    {/* padding geral do conteúdo */}
                    <div className="grid flex-1 auto-rows-min gap-6 mt-5 mb-5">
                      <div className="grid gap-3 w-[80%]">
                        <Label htmlFor="sheet-demo-name">Pegar clube id:</Label>
                        <p className="w-[80%] text-sm">
                          Acesse a página{'  →  '}
                          <a
                            href="https://central.astronmembers.com.br/clubs"
                            target="_blank"
                            rel="noreferrer"
                            className="underline text-blue-600 hover:text-blue-800 text-xs"
                          >
                            https://central.astronmembers.com.br/clubs
                          </a>{' '}
                          → escolhe o clube e vai em configurações → pegar o id que está na url de
                          configurações depois de <code>info</code> (exemplo:{' '}
                          <a
                            href="https://central.astronmembers.com.br/club/info/12475"
                            target="_blank"
                            rel="noreferrer"
                            className="underline text-blue-600 hover:text-blue-800 text-xs"
                          >
                            https://central.astronmembers.com.br/club/info/12475
                          </a>
                          ), pegar <code>12475</code>.
                        </p>
                      </div>

                      <div className="grid gap-3 w-[80%]">
                        <Label htmlFor="sheet-demo-name">Pegar curso id:</Label>
                        <p className="w-[80%] text-sm">
                          Na página que você acabou de entrar em configurações, aba resumo, acessar
                          club → escolhe qualquer aula e entra → na url da aula pegar o primeiro
                          número da url (exemplo:{' '}
                          <a
                            href="https://destravandomentes.astronmembers.com/curso/%F0-primeiros-passos-%F0-/143977/816483"
                            target="_blank"
                            rel="noreferrer"
                            className="underline text-blue-600 hover:text-blue-800 text-xs"
                          >
                            https://destravandomentes.astronmembers.com/curso/%F0-primeiros-passos-%F0-/143977/816483
                          </a>
                          ) pegar <code>143977</code>.
                        </p>
                      </div>
                    </div>

                    <SheetFooter className="flex justify-center items-center  py-4">
                      <SheetClose asChild>
                        <Button variant="outline" className="mx-auto">
                          Sair
                        </Button>
                      </SheetClose>
                    </SheetFooter>
                  </SheetContent>

                  {editingId === ev.id ? (
                    // Modo edição
                    <div className="space-y-3">
                      {isAstronCourses ? (
                        // Interface especial para AstronCourses
                        <div className="space-y-3 border rounded-lg p-4 ">
                          {(astronCourses[ev.id] || []).map((course, index) => (
                            <div key={index} className="flex gap-2 items-start">
                              <div className="flex-1 grid grid-cols-2 gap-2">
                                <div className="space-y-1">
                                  <Label htmlFor={`clubId-${ev.id}-${index}`} className="text-xs">
                                    Clube ID
                                  </Label>
                                  <Input
                                    id={`clubId-${ev.id}-${index}`}
                                    value={course.clubId}
                                    onChange={(e) =>
                                      handleAstronCourseChange(
                                        ev.id,
                                        index,
                                        'clubId',
                                        e.target.value,
                                      )
                                    }
                                    placeholder="ID do Clube"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label htmlFor={`courseId-${ev.id}-${index}`} className="text-xs">
                                    Curso ID
                                  </Label>
                                  <Input
                                    id={`courseId-${ev.id}-${index}`}
                                    value={course.courseId}
                                    onChange={(e) =>
                                      handleAstronCourseChange(
                                        ev.id,
                                        index,
                                        'courseId',
                                        e.target.value,
                                      )
                                    }
                                    placeholder="ID do Curso"
                                  />
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveAstronCourse(ev.id, index)}
                                className="h-10 w-10 p-0 mt-6"
                                disabled={(astronCourses[ev.id] || []).length === 1}
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          ))}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleAddAstronCourse(ev.id)}
                            className="w-full"
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Adicionar Turma
                          </Button>
                        </div>
                      ) : (
                        // Input normal para outras configs
                        <Input
                          id={ev.key}
                          value={editValues[ev.id] || ''}
                          onChange={(e) => handleValueChange(ev.id, e.target.value)}
                          autoFocus
                        />
                      )}
                      <div className="flex gap-2">
                        <Button variant="destructive" size="sm" onClick={handleCancel}>
                          Cancelar
                        </Button>
                        <Button
                          className="w-[120px]"
                          type="submit"
                          size="sm"
                          onClick={() => handleSaveConfig(ev)}
                        >
                          Salvar
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // Modo visualização (desabilitado)
                    <div className="flex items-center gap-2">
                      <Input
                        id={ev.key}
                        type={ev.key === 'password' ? 'password' : 'text'}
                        value={
                          isAstronCourses
                            ? `${
                                (astronCourses[ev.id] || JSON.parse(ev.value || '[]')).length
                              } turma(s) configurada(s)`
                            : ev.value
                        }
                        disabled
                        className="flex-1"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditClick(ev)}
                        className="h-10 w-10 p-0"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </Sheet>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default Configs;
